import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  IconButton,
  createFilterOptions,
  FilterOptionsState,
} from '@mui/material';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { useTranslation } from 'react-i18next';

import './CustomTimeSelector.scss';

import {
  formatTimeFromDayjs,
  generateTimeOptions,
  isValidTimeFormat,
  parseTimeString,
  buildUserTypedOptions,
} from '../../../helpers/';

dayjs.extend(customParseFormat);

/** parseTimeString uses today's date; preserve the existing value's calendar day when editing time. */
const mergeParsedTimeOntoValue = (base: Dayjs, parsed: Dayjs): Dayjs =>
  base.hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0);

interface CustomTimeSelectorProps {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  className?: string;
  size?: 'small' | 'medium';
  interval?: number;
  amPm?: boolean; // true => 12h, false => 24h
  showClearButton?: boolean;
  fontWeight?: 'normal' | 'bold';
  open?: boolean;
  setOpen?: (open: boolean) => void;
  autocompleteClassName?: string;
  paperClassName?: string;
  placeholder?: string;
  defaultTimeInSelector?: Dayjs | null;
}

const defaultFilter = createFilterOptions<string>();

export default function CustomTimeSelector({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  className,
  size = 'medium',
  interval = 15,
  amPm = false, // default 24h
  showClearButton = false,
  fontWeight = 'normal',
  open,
  setOpen,
  autocompleteClassName,
  paperClassName,
  placeholder,
  defaultTimeInSelector,
}: CustomTimeSelectorProps) {
  const { t } = useTranslation();
  const inputEl = useRef<HTMLInputElement | null>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const [localOpen, setLocalOpen] = useState(false);
  const isOpen = open ?? localOpen;

  // Shown inside the input when a value is selected
  const displayValue = useMemo(() => {
    if (!value) return null;
    return formatTimeFromDayjs(value, amPm ? '12h' : '24h');
  }, [value, amPm]);

  // Raw input text
  const [inputValue, setInputValue] = useState('');

  // Keep inputValue in sync with external value
  useEffect(() => {
    setInputValue(displayValue || '');
  }, [displayValue]);

  const handleOpen = (open: boolean) => {
    setLocalOpen(open);
    setOpen?.(open);
  };

  // Smart validation:
  //  - allow "^\d{1,2}:$" (e.g., "2:") while typing
  //  - mark "^\d{1,2}:\d$" (e.g., "2:2") as invalid immediately
  //  - otherwise fall back to strict format validation
  const isHardInvalid = useMemo(() => {
    if (!inputValue) return false;

    if (/^\d{1,2}:$/.test(inputValue)) return false; // still typing minutes
    if (/^\d{1,2}:\d$/.test(inputValue)) return true; // clearly invalid in the middle

    return !isValidTimeFormat(inputValue, amPm ? '12h' : '24h');
  }, [inputValue, amPm]);

  const hasError = error || isHardInvalid;

  // Time options based on interval + format
  const timeOptions = useMemo(
    () => generateTimeOptions(interval, amPm ? '12h' : '24h'),
    [interval, amPm]
  );

  // Function to find the closest time option to the current value or default time
  const findClosestTimeIndex = useMemo(() => {
    if (!timeOptions.length) return 0;

    // Use selected value if available, otherwise fall back to defaultTimeInSelector
    const timeToUse = value || defaultTimeInSelector;
    if (!timeToUse) return 0;

    const currentTimeStr = formatTimeFromDayjs(timeToUse, amPm ? '12h' : '24h');
    const currentTime = parseTimeString(currentTimeStr, amPm ? '12h' : '24h');

    if (!currentTime || !currentTime.isValid()) return 0;

    let closestIndex = 0;
    let minDiff = Infinity;

    timeOptions.forEach((timeStr, index) => {
      const time = parseTimeString(timeStr, amPm ? '12h' : '24h');
      if (time && time.isValid()) {
        const diff = Math.abs(currentTime.diff(time, 'minutes'));
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = index;
        }
      }
    });

    return closestIndex;
  }, [value, defaultTimeInSelector, timeOptions, amPm]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputEl.current?.focus();

        // Scroll to the closest time option
        if (listboxRef.current) {
          const listbox = listboxRef.current;
          const options = listbox.querySelectorAll('[role="option"]');
          const targetIndex = findClosestTimeIndex;

          if (options[targetIndex]) {
            const targetOption = options[targetIndex] as HTMLElement;
            const listboxRect = listbox.getBoundingClientRect();
            const optionRect = targetOption.getBoundingClientRect();

            // Calculate the scroll position to center the option
            const scrollTop =
              targetOption.offsetTop - listboxRect.height / 2 + optionRect.height / 2;

            listbox.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth',
            });
          }
        }
      }, 50); // Small delay to ensure the listbox is rendered
    }
  }, [isOpen, findClosestTimeIndex]);

  // Show default filtered list and inject user-typed exact time(s) when hh:mm is complete
  const filterOptions = (options: string[], state: FilterOptionsState<string>) => {
    const { inputValue } = state;

    // baseline filtering keeps "2:" behavior (shows the 2:00, 2:15, ... list)
    let filtered = defaultFilter(options, state);

    // inject "2:22 AM"/"2:22 PM" (12h) or "02:22" (24h)
    const injected = buildUserTypedOptions(inputValue.trim(), !!amPm);
    for (const opt of injected) {
      if (!filtered.some((x) => x.toLowerCase() === opt.toLowerCase())) {
        filtered.unshift(opt);
      }
    }

    return filtered;
  };

  const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (_: React.SyntheticEvent, newValue: string | null) => {
    if (newValue) {
      const parsedDayjs = parseTimeString(newValue, amPm ? '12h' : '24h');
      if (parsedDayjs && parsedDayjs.isValid()) {
        setInputValue(newValue);
        const next = value ? mergeParsedTimeOntoValue(value, parsedDayjs) : parsedDayjs;
        onChange?.(next);
      } else {
        // keep what the user typed so they can correct it
        setInputValue(newValue);
      }
    } else {
      setInputValue('');
      onChange?.(null);
    }
  };

  const handleClear = () => {
    setInputValue('');
    onChange?.(null);
  };

  const commitIfNeeded = () => {
    const raw = inputValue.trim();

    // empty -> clear
    if (!raw) {
      onChange?.(null);
      return;
    }

    const parsed = parseTimeString(raw, amPm ? '12h' : '24h');
    if (parsed && parsed.isValid()) {
      const next = value ? mergeParsedTimeOntoValue(value, parsed) : parsed;
      onChange?.(next);
    }
  };
  return (
    <div className={clsx('custom-time-selector', className)}>
      <Autocomplete<string, false, false, true>
        freeSolo
        options={timeOptions}
        filterOptions={filterOptions}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={displayValue}
        onChange={handleChange}
        onBlur={() => {
          handleOpen(false);
          commitIfNeeded();
        }}
        open={isOpen}
        onClose={(_, reason) => {
          handleOpen(false);
          // reason can be: 'blur' | 'toggleInput' | 'escape' | 'backdropClick' | 'removeOption' | 'selectOption'...
          if (reason !== 'selectOption') commitIfNeeded();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.stopPropagation();
            handleOpen(false);
            commitIfNeeded();
          }
        }}
        disabled={disabled}
        size={size}
        autoHighlight
        openOnFocus
        handleHomeEndKeys
        isOptionEqualToValue={(opt, val) => opt === val}
        className={clsx('custom-time-selector__autocomplete', autocompleteClassName)}
        renderInput={(params) => (
          <TextField
            {...params}
            required={false}
            onChange={() => handleOpen(true)}
            className={clsx('custom-time-selector__text-field', {
              'custom-time-selector__text-field--small': size === 'small',
              'custom-time-selector__text-field--bold': fontWeight === 'bold',
            })}
            InputLabelProps={{
              ...params.InputLabelProps,
            }}
            placeholder={!value ? placeholder : undefined}
            onFocus={() => handleOpen(true)}
            InputProps={{
              ...params.InputProps,
              endAdornment:
                showClearButton && value && !disabled ? (
                  <IconButton
                    onClick={handleClear}
                    size="small"
                    className="custom-time-selector__clear-button"
                    style={{
                      width: size === 'small' ? 16 : 20,
                      height: size === 'small' ? 16 : 20,
                    }}
                    disabled={disabled}
                  >
                    <Icon path={mdiClose} size={size === 'small' ? '12px' : '16px'} />
                  </IconButton>
                ) : null,
            }}
            inputRef={inputEl}
          />
        )}
        ListboxProps={{
          className: 'custom-time-selector__listbox',
          ref: listboxRef,
        }}
        slotProps={{
          paper: { className: clsx('custom-time-selector__paper', paperClassName) },
        }}
      />

      {hasError && (
        <div className="custom-time-selector__error-box">
          <span className="custom-time-selector__error-text">
            {helperText || t('task.time.invalidTime')}
          </span>
        </div>
      )}
    </div>
  );
}
